import store from '@/store'
import { convertFiatToUsdt, formatCurrencyAmount, formatUsdtAmount, formatWalletProcDate } from '@/utils/format'
import { extractTetherAddress } from '@/utils/qrCode'
import { applyWithdrawUsdtFee } from '@/utils/usdtNetworkFee'
import i18n from '@/utils/i18n'
import { isKrwCurrency, resolveAmountPlaceholderValue } from '@/utils/currencyCode'
import { isPgPaymentMethod } from '@/utils/paymentMethod'
import { resolveSiteCurrency } from '@/utils/siteBrand'

function siteCurrency () {
  return resolveSiteCurrency(store.state.siteInfo)
}

function formatSiteCurrency (value) {
  return formatCurrencyAmount(value, siteCurrency())
}

function isKrwSite () {
  return isKrwCurrency(siteCurrency())
}

function isPgSite () {
  return isPgPaymentMethod(store.state.siteInfo)
}

export const CHARGE_LIST_PAGE_SIZE = 10
export const WITHDRAW_LIST_PAGE_SIZE = 10
/** @deprecated getMinChargeAmount() 사용 — 백엔드 SiteAmountRules와 맞춤 */
export const MIN_CHARGE_AMOUNT = 10
/** @deprecated getMinWithdrawAmount() 사용 */
export const MIN_WITHDRAW_AMOUNT = 10
/** PG 최소 출금 금액 ($30) — 백엔드 SiteAmountRules 와 동일 */
export const PG_MIN_WITHDRAW_AMOUNT = 30

/** USD 입출금 빠른 금액 버튼 */
export const WALLET_QUICK_AMOUNTS_USD = [10, 30, 50, 100, 500, 1000]
/** PG USD 출금 빠른 금액 (최소 $30) */
export const WALLET_QUICK_AMOUNTS_PG_USD = [30, 50, 100, 500, 1000]
/** KRW 입출금 빠른 금액 버튼 */
export const WALLET_QUICK_AMOUNTS_KRW = [10000, 30000, 50000, 100000, 500000, 1000000]
/** @deprecated getWalletQuickAmounts() 사용 */
export const WALLET_QUICK_AMOUNTS = WALLET_QUICK_AMOUNTS_USD

/** 백엔드 SiteAmountRules.MinChargeAmount 와 동일 (비-PG) */
export function getMinChargeAmount () {
  return isKrwSite() ? 100 : 10
}

/** 백엔드 SiteAmountRules.MinWithdrawAmount 와 동일 */
export function getMinWithdrawAmount () {
  if (isPgSite()) return PG_MIN_WITHDRAW_AMOUNT
  return isKrwSite() ? 100 : 10
}

export function getAmountPlaceholder () {
  const currency = siteCurrency()
  const example = resolveAmountPlaceholderValue(currency)
  return i18n.t('wallet.amountPlaceholder', {
    amount: formatCurrencyAmount(example, currency)
  })
}

/**
 * PG(payment_method=4) 입금 고정 금액 (wayCode별).
 * PG 가맹점 콘솔에서 허용하는 금액만 주문 생성이 가능하므로 직접 입력을 막고 선택만 받는다.
 * 변경 시 백엔드 PgAmountOptions 도 함께 수정해야 한다.
 */
export const PG_DEPOSIT_AMOUNTS_CASHAPP = [
  9.99, 14.99, 17.99, 19.99, 24.99, 29.99,
  30.99, 39.99, 49.99, 59.99, 99.99, 124.99,
  129.99, 149.99, 199.99, 300, 400, 500
]

export const PG_DEPOSIT_AMOUNTS_CHIME = [
  20, 25, 30, 31, 40, 50, 60,
  100, 125, 130, 150, 200, 300, 400, 500
]

/** applepay / googlepay 고정 금액 (300/400/500 없음) */
export const PG_DEPOSIT_AMOUNTS_APPLE_GOOGLE = [
  9.99, 14.99, 17.99, 19.99, 24.99, 29.99,
  30.99, 39.99, 49.99, 59.99, 99.99, 124.99,
  129.99, 149.99, 199.99
]

/** @deprecated 하위 호환 — getPgDepositAmounts(wayCode) 사용 */
export const PG_DEPOSIT_AMOUNTS = PG_DEPOSIT_AMOUNTS_CASHAPP

export function getPgDepositAmounts (wayCode) {
  const code = String(wayCode || '').trim().toLowerCase()
  if (code === 'chime') return PG_DEPOSIT_AMOUNTS_CHIME
  if (code === 'applepay' || code === 'googlepay') return PG_DEPOSIT_AMOUNTS_APPLE_GOOGLE
  // cashapp / ecashapp
  return PG_DEPOSIT_AMOUNTS_CASHAPP
}

/** PG 입금 최소 금액 (wayCode별, 미지정 시 전 way 최솟값) */
export function getPgMinDepositAmount (wayCode) {
  const code = String(wayCode || '').trim().toLowerCase()
  if (
    code === 'cashapp' || code === 'ecashapp' || code === 'chime' ||
    code === 'googlepay' || code === 'applepay'
  ) {
    const amounts = getPgDepositAmounts(code)
    return Math.min(...amounts)
  }
  return Math.min(
    Math.min(...PG_DEPOSIT_AMOUNTS_CASHAPP),
    Math.min(...PG_DEPOSIT_AMOUNTS_CHIME),
    Math.min(...PG_DEPOSIT_AMOUNTS_APPLE_GOOGLE)
  )
}

/** PG 고정 금액 라벨 ($9.99, $300) */
export function formatPgAmountLabel (amount) {
  const num = Number(amount) || 0
  const decimals = Number.isInteger(num) ? 0 : 2
  return `${siteCurrency()}${num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: 2
  })}`
}

/** 빠른 금액 버튼 라벨 ($10, $1,000 …) */
export function formatQuickAmountLabel (amount) {
  const num = Math.floor(Number(amount) || 0)
  return `${siteCurrency()}${num.toLocaleString('en-US')}`
}

export function getWalletQuickAmounts () {
  let amounts
  if (isKrwSite()) {
    amounts = WALLET_QUICK_AMOUNTS_KRW
  } else if (isPgSite()) {
    amounts = WALLET_QUICK_AMOUNTS_PG_USD
  } else {
    amounts = WALLET_QUICK_AMOUNTS_USD
  }
  return amounts.map((amount) => ({
    amount,
    label: formatQuickAmountLabel(amount)
  }))
}

const WITHDRAW_REQUEST_TOOMANY_CODE = 2026
const WITHDRAW_MONEY_MIN_CODE = 2027
const BALANCE_NOT_ENOUGH_CODE = 2006
const WITHDRAWER_LIMIT_ERROR_CODE = 2043
const PG_CREATE_FAILED_CODE = 2053
const PG_NOT_CONFIGURED_CODE = 2052
const PG_WAYCODE_REQUIRED_CODE = 2054

export function resolveWithdrawRequestError (body) {
  if (!body) return i18n.t('wallet.withdrawFail')
  const code = Number(body.code)
  const message = String(body.message || '').toUpperCase()
  if (code === WITHDRAW_REQUEST_TOOMANY_CODE || message === 'CHARGE_REQUEST_TOOMANY') {
    return i18n.t('wallet.withdrawPending')
  }
  if (code === WITHDRAW_MONEY_MIN_CODE || message === 'WITHDRAW_MONEY_MIN') {
    return i18n.t('wallet.withdrawMinAmount', {
      amount: formatSiteCurrency(getMinWithdrawAmount())
    })
  }
  if (code === BALANCE_NOT_ENOUGH_CODE || message === 'BALANCE_NOT_ENOUGH') {
    return i18n.t('wallet.withdrawExceedBalance')
  }
  if (code === WITHDRAWER_LIMIT_ERROR_CODE || message === 'WITHDRAWER_LIMIT_ERROR') {
    const minutes = body.message ? String(body.message).trim() : ''
    return minutes
      ? i18n.t('wallet.withdrawLimitMinutes', { minutes })
      : i18n.t('wallet.withdrawLimitGeneric')
  }
  if (code === PG_CREATE_FAILED_CODE || message === 'PG_CREATE_FAILED') {
    return i18n.t('wallet.pgWithdrawCreateFail')
  }
  if (code === PG_NOT_CONFIGURED_CODE || message === 'PG_NOT_CONFIGURED') {
    return i18n.t('wallet.pgWithdrawCreateFail')
  }
  if (code === PG_WAYCODE_REQUIRED_CODE || message === 'PG_WAYCODE_REQUIRED') {
    return i18n.t('wallet.pgWayCodeRequired')
  }
  if (body.message) return body.message
  return i18n.t('wallet.withdrawFail')
}

export function normalizeChargeList (payload, { completedOnly = false } = {}) {
  if (!payload) return []
  const list = Array.isArray(payload.list) ? payload.list : []
  return list.filter(item => {
    if (!item || item.idx == null) return false
    if (completedOnly && Number(item.status) !== 4) return false
    return true
  })
}

export function chargeStatusLabel (status) {
  const value = Number(status)
  if (value === 1) return i18n.t('wallet.statusRequest')
  if (value === 2) return i18n.t('wallet.statusWaiting')
  if (value === 4) return i18n.t('wallet.statusComplete')
  if (value === 8) return i18n.t('wallet.statusCancel')
  if (value === 16) return i18n.t('wallet.statusDelete')
  return i18n.t('wallet.statusUnknown')
}

export function chargeStatusClass (status) {
  const value = Number(status)
  if (value === 1) return 'wallet-status--request'
  if (value === 2) return 'wallet-status--waiting'
  if (value === 4) return 'wallet-status--complete'
  if (value === 8) return 'wallet-status--cancel'
  return 'wallet-status--unknown'
}

/** 입금 구분: 1=일반 충전요청, 2=강제충전 */
export function chargeTypeLabel (type) {
  const value = Number(type)
  if (value === 1) return i18n.t('wallet.chargeTypeNormal')
  if (value === 2) return i18n.t('wallet.chargeTypeForce')
  return i18n.t('wallet.chargeTypeOther')
}

export function chargeTypeClass (type) {
  const value = Number(type)
  if (value === 1) return 'wallet-type--normal'
  if (value === 2) return 'wallet-type--force'
  return 'wallet-type--unknown'
}

/** 신청·대기 등 미처리 상태 */
export function isChargePending (status) {
  const value = Number(status)
  return value === 1 || value === 2
}

export function formatChargeProcDate (row) {
  return formatWalletProcDate(row && row.procdate, {
    pending: row ? isChargePending(row.status) : true
  })
}

const TETHER_CHARGE_ACCOUNT_PREFIX = '테더:'
const TETHER_CHARGE_ACCOUNT_LABEL = 'USDT'

export function formatWalletAddressShort (address) {
  const text = String(address || '').trim()
  if (!text) return ''
  if (text.length <= 22) return text
  return `${text.slice(0, 10)}…${text.slice(-8)}`
}

export function parseTetherChargeAccount (account) {
  const raw = String(account || '').trim()
  if (!raw) return null

  if (raw.startsWith(TETHER_CHARGE_ACCOUNT_PREFIX)) {
    const address = raw.slice(TETHER_CHARGE_ACCOUNT_PREFIX.length).trim()
    return address ? { label: TETHER_CHARGE_ACCOUNT_LABEL, address } : null
  }

  const usdtMatch = raw.match(/^USDT\s*\/\s*(.+)$/i)
  if (usdtMatch && usdtMatch[1]) {
    const address = String(usdtMatch[1]).trim()
    return address ? { label: TETHER_CHARGE_ACCOUNT_LABEL, address } : null
  }

  return null
}

export function isTetherChargeAccount (account) {
  return parseTetherChargeAccount(account) != null
}

export function formatTetherAccountInfo (account) {
  const parsed = parseTetherChargeAccount(account)
  if (!parsed) return null
  const shortAddress = formatWalletAddressShort(parsed.address)
  if (!shortAddress) return null
  return `${parsed.label} / ${shortAddress}`
}

export function formatTetherAccountInfoTitle (account) {
  const parsed = parseTetherChargeAccount(account)
  if (!parsed) return ''
  return `${parsed.label} / ${parsed.address}`
}

/** 테더 입금 요청 account: USDT / {TRC20주소} */
export function buildTetherChargeAccount (tetherAddress) {
  const address = String(tetherAddress || '').trim()
  if (!address) return ''
  const parsed = parseTetherChargeAccount(address)
  const normalized = parsed ? parsed.address : address
  return `${TETHER_CHARGE_ACCOUNT_LABEL} / ${normalized}`
}

/** 계좌 입금 요청 account: 입금자명:… 은행명:… 계좌정보:… */
export function buildBankChargeAccount ({ bankMaster, bankName, bankAccount }) {
  const master = String(bankMaster || '').trim()
  const name = String(bankName || '').trim()
  const account = String(bankAccount || '').trim()
  if (!master || !name || !account) return ''

  return `입금자명:${master} 은행명:${name} 계좌정보:${account}`
}

/** 문자열·객체에서 { ... } JSON 블록 추출 */
export function extractJsonObjectFromText (value) {
  if (value == null || value === '') return null
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value
  }

  const str = String(value).trim()
  if (!str) return null

  if (str.charAt(0) === '{') {
    try {
      const parsed = JSON.parse(str)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed
    } catch (err) {
      // fall through
    }
  }

  const start = str.indexOf('{')
  const end = str.lastIndexOf('}')
  if (start >= 0 && end > start) {
    try {
      const parsed = JSON.parse(str.slice(start, end + 1))
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed
    } catch (err) {
      return null
    }
  }

  return null
}

/** charge_req 응답 bank_name — JObject 또는 문자열 내 JSON */
export function parseChargeBankNameField (bankNameRaw) {
  return extractJsonObjectFromText(bankNameRaw)
}

function pickTetherAddressFromBankNameObject (obj) {
  if (!obj) return ''
  const tether = obj.Tether || obj.tether || obj.TETHER || ''
  return String(tether).trim()
}

function pickTetherAddressFromText (text) {
  const obj = extractJsonObjectFromText(text)
  const fromObj = pickTetherAddressFromBankNameObject(obj)
  if (fromObj) return fromObj

  const raw = String(text || '')
  const match = raw.match(/"Tether"\s*:\s*"([^"]+)"/i)
  if (match && match[1]) return String(match[1]).trim()

  const legacy = raw.replace(/^테더주소\s*안내\s*:\s*/i, '').trim()
  if (legacy && legacy.indexOf('{') < 0 && legacy.indexOf('"Tether"') < 0) {
    return legacy
  }

  return ''
}

/** charge_req 응답 — 테더 입금: Tether 주소만 */
export function extractTetherFromChargeData (data) {
  if (!data) return ''

  const direct = String(data.usdt_to_address || data.trc20_address || '').trim()
  if (direct && /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(direct)) {
    return direct
  }

  const fromBankName = pickTetherAddressFromText(data.bank_name)
  if (fromBankName) return fromBankName

  if (data.bankinfo) {
    const fromBankinfo = pickTetherAddressFromText(data.bankinfo)
    if (fromBankinfo) return fromBankinfo

    const bankinfoText = String(data.bankinfo || '')
    if (bankinfoText.indexOf('{') < 0) {
      return extractTetherAddress(data.bankinfo)
    }
  }

  return ''
}

function pickBankNameFromText (text) {
  const obj = extractJsonObjectFromText(text)
  if (!obj) return ''
  return String(obj.bank_name || obj.bankName || '').trim()
}

/** "예금주 계좌번호 { ... }" — JSON 앞 공백 구분: 1=예금주, 2=계좌번호 */
function parseBankPrefixFromText (text) {
  const str = String(text || '').trim()
  if (!str) {
    return { bankMaster: '', bankAccount: '' }
  }

  const brace = str.indexOf('{')
  const prefix = brace >= 0 ? str.slice(0, brace).trim() : str
  if (!prefix) {
    return { bankMaster: '', bankAccount: '' }
  }

  const parts = prefix.split(/\s+/).filter(function (part) {
    return !!part
  })

  return {
    bankMaster: parts[0] ? String(parts[0]).trim() : '',
    bankAccount: parts[1] ? String(parts[1]).trim() : ''
  }
}

/** charge_req 응답 — 계좌 입금: bank_name(JSON), bank_account·bank_master(앞쪽 텍스트) */
export function extractBankFromChargeData (data) {
  if (!data) {
    return { bankName: '', bankAccount: '', bankMaster: '' }
  }

  const sourceText = data.bank_name || data.bankinfo || ''
  const obj = parseChargeBankNameField(sourceText)
  const prefix = parseBankPrefixFromText(sourceText)

  let bankName = pickBankNameFromText(sourceText)
  if (!bankName && obj) {
    bankName = String(obj.bank_name || obj.bankName || '').trim()
  }

  const bankAccount = String(
    data.bank_account ||
    prefix.bankAccount ||
    (obj && (obj.bank_account || obj.bankAccount)) ||
    ''
  ).trim()

  const bankMaster = String(
    data.bank_master ||
    prefix.bankMaster ||
    (obj && (obj.bank_master || obj.bankMaster)) ||
    ''
  ).trim()

  return {
    bankName: bankName,
    bankAccount: bankAccount,
    bankMaster: bankMaster
  }
}

export function formatBankDepositCopyText ({ bankName, bankAccount, bankMaster } = {}) {
  const lines = []
  if (bankName) lines.push(`${i18n.t('wallet.bankName')}: ${bankName}`)
  if (bankAccount) lines.push(`${i18n.t('wallet.accountNumber')}: ${bankAccount}`)
  if (bankMaster) lines.push(`${i18n.t('wallet.accountHolder')}: ${bankMaster}`)
  return lines.join('\n')
}

function pickMemoField (memo, re) {
  const m = String(memo || '').match(re)
  return m ? m[1] : ''
}

function parseUsdtPartialForceMemo (memo) {
  const text = String(memo || '')
  if (!text.includes('[USDT부분입금-강제충전]')) return null
  return {
    tag: 'USDT부분입금',
    originIdx: pickMemoField(text, /원요청idx=(\d+)/),
    requestUsdt: pickMemoField(text, /요청=([\d.]+)USDT/),
    actualUsdt: pickMemoField(text, /실제=([\d.]+)USDT/),
    rate: pickMemoField(text, /환율=([\d.]+)/),
    krw: pickMemoField(text, /충전=([\d,]+)원/),
    tx: pickMemoField(text, /tx=([a-fA-F0-9]+)/)
  }
}

function parseUsdtMismatchCancelMemo (memo) {
  const text = String(memo || '')
  if (!text.includes('[USDT금액불일치-취소]')) return null
  return {
    tag: 'USDT금액불일치',
    requestUsdt: pickMemoField(text, /요청=([\d.]+)USDT/),
    actualUsdt: pickMemoField(text, /실제=([\d.]+)USDT/),
    tx: pickMemoField(text, /tx=([a-fA-F0-9]+)/),
    note: text.includes('강제충전') ? '실제입금액은 강제충전으로 처리' : ''
  }
}

/** 입금내역 메모 요약 (목록용) */
export function formatChargeMemoShort (memo) {
  const text = String(memo || '').replace(/\s+/g, ' ').trim()
  if (!text) return ''

  const partial = parseUsdtPartialForceMemo(text)
  if (partial) {
    const parts = [partial.tag]
    if (partial.actualUsdt && partial.krw) {
      parts.push(`실제 ${partial.actualUsdt}USDT → ${partial.krw}원`)
    } else if (partial.actualUsdt) {
      parts.push(`실제 ${partial.actualUsdt}USDT`)
    }
    return parts.join(' | ')
  }

  const mismatch = parseUsdtMismatchCancelMemo(text)
  if (mismatch) {
    const parts = [mismatch.tag]
    if (mismatch.requestUsdt && mismatch.actualUsdt) {
      parts.push(`요청 ${mismatch.requestUsdt} / 실제 ${mismatch.actualUsdt}USDT`)
    }
    return parts.join(' | ')
  }

  const max = 48
  return text.length <= max ? text : `${text.slice(0, max)}...`
}

/** 입금내역 메모 전체 (호버 툴팁용) */
export function formatChargeMemoTooltip (memo) {
  const text = String(memo || '').trim()
  if (!text) return ''

  const partial = parseUsdtPartialForceMemo(text)
  if (partial) {
    return [
      `[${partial.tag}]`,
      partial.originIdx ? `원요청 idx: ${partial.originIdx}` : '',
      partial.requestUsdt ? `요청 USDT: ${partial.requestUsdt}` : '',
      partial.actualUsdt ? `실제 USDT: ${partial.actualUsdt}` : '',
      partial.rate ? `환율: ${partial.rate}` : '',
      partial.krw ? `충전 금액: ${partial.krw}원` : '',
      partial.tx ? `tx: ${partial.tx}` : ''
    ].filter(Boolean).join('\n')
  }

  const mismatch = parseUsdtMismatchCancelMemo(text)
  if (mismatch) {
    return [
      `[${mismatch.tag}]`,
      mismatch.requestUsdt ? `요청 USDT: ${mismatch.requestUsdt}` : '',
      mismatch.actualUsdt ? `실제 USDT: ${mismatch.actualUsdt}` : '',
      mismatch.note || '',
      mismatch.tx ? `tx: ${mismatch.tx}` : ''
    ].filter(Boolean).join('\n')
  }

  return text
}

/** 입금 요청 memo: $100.00 = 100.00USDT */
export function buildChargeRequestMemo (fiatAmount, ratePerUsdt) {
  const fiat = formatSiteCurrency(fiatAmount)
  const baseUsdt = convertFiatToUsdt(fiatAmount, ratePerUsdt)
  if (baseUsdt == null) return ''
  const usdtStr = formatUsdtAmount(baseUsdt, { maxDecimals: 2, minDecimals: 2 })
  return i18n.t('wallet.chargeMemo', { krw: fiat, usdt: usdtStr })
}

/** 출금 요청 memo: $100 = 97.76USDT / fee 2.24USDT */
export function buildWithdrawRequestMemo (fiatAmount, ratePerUsdt, feeUsdt = 0) {
  const fiat = formatSiteCurrency(fiatAmount)
  const baseUsdt = convertFiatToUsdt(fiatAmount, ratePerUsdt)
  if (baseUsdt == null) return ''
  const fee = Number(feeUsdt)
  const safeFee = Number.isFinite(fee) && fee > 0 ? fee : 0
  const netUsdt = applyWithdrawUsdtFee(baseUsdt, safeFee)
  const netStr = formatUsdtAmount(netUsdt, { maxDecimals: 2, minDecimals: 2 })
  const feeStr = formatUsdtAmount(safeFee, { maxDecimals: 2, minDecimals: 2 })
  return i18n.t('wallet.withdrawMemo', { krw: fiat, usdt: netStr, feeUsdt: feeStr })
}

export function normalizeWithdrawList (payload, { completedOnly = false } = {}) {
  if (!payload) return []
  const list = Array.isArray(payload.list) ? payload.list : []
  return list.filter(item => {
    if (!item || item.idx == null) return false
    if (completedOnly && Number(item.status) !== 4) return false
    return true
  })
}

export const withdrawStatusLabel = chargeStatusLabel
export const withdrawStatusClass = chargeStatusClass

/** 출금 구분: 1=일반출금, 2=강제출금 */
export function withdrawTypeLabel (type) {
  const value = Number(type)
  if (value === 1) return i18n.t('wallet.withdrawTypeNormal')
  if (value === 2) return i18n.t('wallet.withdrawTypeForce')
  return i18n.t('wallet.withdrawTypeOther')
}

export function withdrawTypeClass (type) {
  const value = Number(type)
  if (value === 1) return 'wallet-type--normal'
  if (value === 2) return 'wallet-type--force'
  return 'wallet-type--unknown'
}

export function isWithdrawPending (status) {
  return isChargePending(status)
}

export function formatWithdrawProcDate (row) {
  return formatWalletProcDate(row && row.procdate, {
    pending: row ? isWithdrawPending(row.status) : true
  })
}

export function computeTotalBet (sbet, cbet) {
  return (Number(sbet) || 0) + (Number(cbet) || 0)
}

/** 총입금 × 출금베팅비율(%) */
export function computeWithdrawBetThreshold (totalCharge, withdrawLimitPercent) {
  const charge = Number(totalCharge) || 0
  const percent = Number(withdrawLimitPercent) || 0
  return charge * percent / 100
}

/** 총베팅액이 (총입금 × 비율)보다 큰 경우 */
export function isWithdrawBetRequirementMet (totalCharge, totalBet, withdrawLimitPercent) {
  return totalBet > computeWithdrawBetThreshold(totalCharge, withdrawLimitPercent)
}

export function computeWithdrawableBalance ({
  balance,
  bonus,
  totalCharge,
  totalBet,
  withdrawLimitPercent
}) {
  if (
    balance == null ||
    bonus == null ||
    totalCharge == null ||
    totalBet == null ||
    withdrawLimitPercent == null
  ) {
    return null
  }
  const money = Number(balance) || 0
  const bonusAmount = Number(bonus) || 0
  if (isWithdrawBetRequirementMet(totalCharge, totalBet, withdrawLimitPercent)) {
    return Math.max(0, money)
  }
  return Math.max(0, money - bonusAmount)
}
